import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatIcelandicNumber } from '@/utils/constants';

export const EmailPreviewDialog = ({ show, onClose, data, onDataChange, apiUrl }) => {
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!data.to_email) {
      toast.error("Netfang vantar");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.to_email)) {
      toast.error("Ógilt netfang");
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${apiUrl}/send-offer-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        toast.success(`Tilboð sent á ${data.to_email}`);
        onClose();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Villa við að senda tölvupóst");
      }
    } catch (err) {
      toast.error("Villa við að senda tölvupóst");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={show} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-green-600" />
            Senda tilboð
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium">Netfang viðtakanda</Label>
            <Input
              type="email"
              value={data.to_email}
              onChange={(e) => onDataChange({ ...data, to_email: e.target.value })}
              placeholder="netfang@domain.is"
              className="mt-1"
            />
          </div>

          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="font-medium mb-3 text-sm">Forskoðun á tilboði</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Tegund:</span>
                <span className="font-medium">{data.loan_type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Viðtakandi:</span>
                <span className="font-medium">{data.recipient_name}</span>
              </div>
              <hr className="my-2" />
              <div className="flex justify-between">
                <span className="text-gray-600">Lánsfjárhæð:</span>
                <span>{formatIcelandicNumber(data.loan_amount)} kr.</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Lántökugjald:</span>
                <span>{formatIcelandicNumber(data.lantokugjald)} kr.</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Heildarfjárhæð:</span>
                <span>{formatIcelandicNumber(data.total_loan)} kr.</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Vextir:</span>
                <span>{data.interest_rate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Lánstími:</span>
                <span>{data.months} mánuðir</span>
              </div>
              <hr className="my-2" />
              <div className="flex justify-between font-medium text-blue-700">
                <span>Mánaðarleg greiðsla:</span>
                <span>{formatIcelandicNumber(data.monthly_payment)} kr.</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={sending}>
              Hætta við
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handleSend}
              disabled={sending}
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sendi...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Senda tilboð
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailPreviewDialog;
